import { useSyncExternalStore } from 'react';
import axios from 'axios';

export type PhotoUploadEntityType = 'artist' | 'album';
export type PhotoUploadStatus = 'uploading' | 'saving' | 'done' | 'error';

export interface PhotoUploadTask {
  key: string;
  requestId: string;
  entityType: PhotoUploadEntityType;
  entityId: string;
  fileName: string;
  progress: number;
  status: PhotoUploadStatus;
  previewUrl?: string;
  remoteUrl?: string;
  error?: string;
  updatedAt: number;
}

interface StartPhotoUploadOptions {
  entityType: PhotoUploadEntityType;
  entityId: string | number;
  file: File;
  uploadUrl: string;
  // uploadResponseData is the raw upload response body, e.g. { suggestedCrop } for artist photos.
  persist: (remoteUrl: string, uploadResponseData?: any) => Promise<unknown>;
  onPersisted?: (remoteUrl: string) => void;
}

const uploads = new Map<string, PhotoUploadTask>();
const listeners = new Set<() => void>();
let cachedSnapshot: PhotoUploadTask[] = [];

const notify = () => {
  listeners.forEach((listener) => listener());
};

const makeKey = (entityType: PhotoUploadEntityType, entityId: string | number) =>
  `${entityType}:${String(entityId)}`;

const setTask = (task: PhotoUploadTask) => {
  uploads.set(task.key, task);
  cachedSnapshot = Array.from(uploads.values()).sort((a, b) => b.updatedAt - a.updatedAt);
  notify();
};

const updateTask = (
  taskKey: string,
  requestId: string,
  updater: (current: PhotoUploadTask) => PhotoUploadTask
) => {
  const current = uploads.get(taskKey);
  if (!current || current.requestId !== requestId) return;
  uploads.set(taskKey, updater(current));
  cachedSnapshot = Array.from(uploads.values()).sort((a, b) => b.updatedAt - a.updatedAt);
  notify();
};

const getSnapshot = () => cachedSnapshot;

const subscribe = (listener: () => void) => {
  listeners.add(listener);
  return () => listeners.delete(listener);
};

const hasPendingUploads = () =>
  Array.from(uploads.values()).some((task) => task.status === 'uploading' || task.status === 'saving');

if (typeof window !== 'undefined') {
  window.addEventListener('beforeunload', (event) => {
    if (!hasPendingUploads()) return;
    event.preventDefault();
    event.returnValue = '';
  });
}

export const useAllPhotoUploads = () =>
  useSyncExternalStore(subscribe, getSnapshot, getSnapshot);

export const usePhotoUpload = (
  entityType: PhotoUploadEntityType,
  entityId?: string | number | null
) => {
  const uploadsSnapshot = useAllPhotoUploads();
  if (entityId == null || entityId === '') return null;
  return uploadsSnapshot.find((task) => task.key === makeKey(entityType, entityId)) ?? null;
};

export const startPhotoUpload = ({
  entityType,
  entityId,
  file,
  uploadUrl,
  persist,
  onPersisted,
}: StartPhotoUploadOptions) => {
  const normalizedEntityId = String(entityId);
  const key = makeKey(entityType, normalizedEntityId);
  const requestId = `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
  const previewUrl = URL.createObjectURL(file);
  const previousTask = uploads.get(key);

  if (previousTask?.previewUrl?.startsWith('blob:')) {
    URL.revokeObjectURL(previousTask.previewUrl);
  }

  setTask({
    key,
    requestId,
    entityType,
    entityId: normalizedEntityId,
    fileName: file.name,
    progress: 0,
    status: 'uploading',
    previewUrl,
    updatedAt: Date.now(),
  });

  const formData = new FormData();
  formData.append('file', file);

  void (async () => {
    try {
      const uploadRes = await axios.post(uploadUrl, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        onUploadProgress: (event) => {
          const total = event.total && event.total > 0 ? event.total : file.size || 1;
          const progress = Math.round((event.loaded * 100) / total);
          updateTask(key, requestId, (current) => ({
            ...current,
            progress: Math.min(progress, 99),
            status: progress >= 99 ? 'saving' : 'uploading',
            updatedAt: Date.now(),
          }));
        },
      });

      const remoteUrl = uploadRes.data?.Location ?? uploadRes.data?.location;
      if (!remoteUrl) {
        throw new Error('Upload response did not include a file URL.');
      }

      updateTask(key, requestId, (current) => ({
        ...current,
        progress: 100,
        status: 'saving',
        remoteUrl,
        updatedAt: Date.now(),
      }));

      await persist(remoteUrl, uploadRes.data);

      updateTask(key, requestId, (current) => ({
        ...current,
        progress: 100,
        status: 'done',
        remoteUrl,
        error: undefined,
        updatedAt: Date.now(),
      }));

      onPersisted?.(remoteUrl);
    } catch (error: any) {
      const message =
        error?.response?.data?.error ||
        error?.response?.data?.details ||
        error?.message ||
        'Upload failed';

      updateTask(key, requestId, (current) => ({
        ...current,
        status: 'error',
        error: String(message),
        updatedAt: Date.now(),
      }));
    }
  })();

  return key;
};
